import {
  ExtraFieldEditor,
  SelectInput,
  TextInput,
  showToast,
  type ExtraField,
  type ExtraFieldType,
} from '@my/ui';
import { Check, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ExtraFieldsListProps {
  /** Current set of custom fields. The component is fully controlled. */
  fields: ExtraField[];
  /** Called whenever the array changes (add, remove, value edit). */
  onChange: (next: ExtraField[]) => void;
}

/**
 * Stack of `ExtraFieldEditor` rows plus a "+ Add field" affordance that flips
 * into a mini label/type form. Owns only the transient add-form state — the
 * resulting array is fully controlled by the parent.
 *
 * Once added, label + type are immutable (matching `ExtraFieldEditor`'s
 * contract); only the `fieldValue` of an existing row is mutable. Renaming
 * or changing type means delete + re-add, which is intentional friction
 * to discourage accidental schema drift on the BE-stored JSON blob.
 *
 * Label uniqueness is enforced case-insensitively at add time — adding a
 * second field with a clashing label surfaces a toast rather than silently
 * shadowing the first one.
 */
export function ExtraFieldsList({ fields, onChange }: ExtraFieldsListProps) {
  const { t } = useTranslation('organisation');
  // Inline draft for "+ Add field" — `isAdding` toggles the mini form
  // (label + type) into view; on confirm we push a new entry into the
  // parent array with an empty value.
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState<{ label: string; type: ExtraFieldType }>({
    label: '',
    type: 'text',
  });

  const handleConfirmAdd = () => {
    const label = draft.label.trim();
    if (!label) {
      showToast({ type: 'error', message: t('extraFields.labelRequired') });
      return;
    }
    if (fields.some((f) => f.fieldLabel.toLowerCase() === label.toLowerCase())) {
      showToast({ type: 'error', message: t('extraFields.duplicateLabel', { label }) });
      return;
    }
    onChange([...fields, { fieldLabel: label, fieldType: draft.type, fieldValue: '' }]);
    setDraft({ label: '', type: 'text' });
    setIsAdding(false);
  };

  const handleCancelAdd = () => {
    setDraft({ label: '', type: 'text' });
    setIsAdding(false);
  };

  const handleValueChange = (index: number, value: string) => {
    const target = fields[index];
    if (!target) return;
    const next = [...fields];
    next[index] = { ...target, fieldValue: value };
    onChange(next);
  };

  const handleDelete = (index: number) => {
    onChange(fields.filter((_, i) => i !== index));
  };

  return (
    <>
      {fields.map((f, idx) => (
        <ExtraFieldEditor
          key={`${f.fieldLabel}-${idx}`}
          field={f}
          onValueChange={(value) => handleValueChange(idx, value)}
          onDelete={() => handleDelete(idx)}
        />
      ))}

      {isAdding ? (
        <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50 p-3">
          <TextInput
            label={t('extraFields.fieldLabel')}
            name="extraFieldLabel"
            autoComplete="off"
            value={draft.label}
            onChange={(e) => setDraft((prev) => ({ ...prev, label: e.target.value }))}
          />
          <SelectInput
            label={t('extraFields.fieldType')}
            name="extraFieldType"
            value={draft.type}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                type: e.target.value as ExtraFieldType,
              }))
            }
            options={[
              { value: 'text', label: t('extraFields.types.text') },
              { value: 'number', label: t('extraFields.types.number') },
              { value: 'date', label: t('extraFields.types.date') },
              { value: 'boolean', label: t('extraFields.types.boolean') },
            ]}
          />
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label={t('extraFields.ariaCancelAdd')}
              onClick={handleCancelAdd}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={t('extraFields.ariaConfirmAdd')}
              onClick={handleConfirmAdd}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          aria-label={t('extraFields.ariaAddField')}
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-1 self-start rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('extraFields.addField')}
        </button>
      )}
    </>
  );
}
